import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { useEffect, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import {
    GetBookingInvoiceDetailsURL,
    getCompanyInformationURL,
    checkInReservationURL
} from "../../../../utils/urls";
import PaymentModal from "./PaymentModal";
import RecordPaymentDialog from "./RecordPaymentDialog";
import styles from "../../lease_management/lease_invoice/invoice.module.css";

const BookingInvoiceDialog = ({ visible, onHide, invoiceId, onCheckInSuccess, onPaymentSuccess }) => {
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showRecordPaymentDialog, setShowRecordPaymentDialog] = useState(false);
    const [showCancellationPolicyDialog, setShowCancellationPolicyDialog] = useState(false);
    const [facilityId, setFacilityId] = useState(null);
    const [companyDetails, setCompanyDetails] = useState(null);
    const [facilityLogo, setFacilityLogo] = useState("");
    const [checkingIn, setCheckingIn] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [selectedAction, setSelectedAction] = useState(null);

    const fetchInvoiceDetails = async () => {
        if (!invoiceId) return;

        try {
            setLoading(true);
            const fetchedFacilityId = await getItem("selectedFacilityId");
            setFacilityId(fetchedFacilityId);

            const response = await makeRequest2(
                `${GetBookingInvoiceDetailsURL}/${fetchedFacilityId}/${invoiceId}`,
                "GET"
            );

            if (response?.success) {
                const invoiceData = response.data.data || response.data;
                setInvoice(invoiceData);
            } else {
                toastify(response?.error || "Failed to fetch invoice details", "error");
                setInvoice(null);
            }
        } catch (error) {
            toastify("Error fetching invoice details", "error");
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanyDetails = async () => {
        try {
            const fetchedFacilityId = await getItem("selectedFacilityId");
            const response = await makeRequest2(`${getCompanyInformationURL}/${fetchedFacilityId}`, "GET");
            if (response.success) {
                setCompanyDetails(response.data);
            }
        } catch (error) {
            // Non-fatal - falls back to placeholder company text
        }
    };

    const getFacilityLogo = () => {
        const storedLogo = localStorage.getItem("selectedFacilityLogo");
        setFacilityLogo(storedLogo || "");
    };

    useEffect(() => {
        if (visible && invoiceId) {
            fetchInvoiceDetails();
            fetchCompanyDetails();
            getFacilityLogo();
        } else if (!visible) {
            setInvoice(null);
        }
    }, [visible, invoiceId]);

    const formatDate = (date) => {
        if (!date) return "";
        return new Date(date).toLocaleDateString();
    };

    const formatAmount = (amount) => {
        if (amount === undefined || amount === null) return "0.00";
        const currencyCode = invoice?.currencyDetails?.code || "USD";
        const numericAmount = parseFloat(amount) || 0;
        return `${currencyCode} ${numericAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const balanceRemaining = invoice?.calculatedData?.balanceRemaining || 0;
    const isFinalCredit = balanceRemaining < 0;

    const shouldShowPayNow = () => {
        if (!invoice) return false;
        if (balanceRemaining <= 0) return false;
        if (invoice.status !== "active") return false;
        return Boolean(invoice.accountNumber || invoice.paymentDetails?.facilityPaymentDetailsId);
    };

    const shouldShowRecordPayment = () => {
        if (!invoice) return false;
        return balanceRemaining > 0;
    };

    const shouldShowCheckIn = () => {
        if (!invoice) return false;
        const reservationStatus = invoice.reservationDetails?.status;
        return reservationStatus === "reserved";
    };

    const handlePaymentSuccess = () => {
        toastify("Payment submitted successfully. Waiting for confirmation...", "success");
        setShowPaymentModal(false);
        setTimeout(() => {
            fetchInvoiceDetails();
            onPaymentSuccess && onPaymentSuccess();
        }, 2000);
    };

    const handlePaymentError = (error) => {
        toastify(error.message || "Payment failed. Please try again.", "error");
    };

    const handleRecordPaymentSuccess = () => {
        setShowRecordPaymentDialog(false);
        fetchInvoiceDetails();
        onPaymentSuccess && onPaymentSuccess();
    };

    const handleCheckIn = async () => {
        const reservationId = invoice?.reservationDetails?.id;
        if (!reservationId || !facilityId) return;

        try {
            setCheckingIn(true);
            const response = await makeRequest2(
                `${checkInReservationURL}/${facilityId}/${reservationId}`,
                "POST",
                {}
            );

            if (response?.success) {
                toastify("Guest checked in successfully", "success");
                fetchInvoiceDetails();
                onCheckInSuccess && onCheckInSuccess(reservationId);
            } else {
                toastify(response?.error || "Failed to check in", "error");
            }
        } catch (error) {
            toastify("Error checking in guest", "error");
        } finally {
            setCheckingIn(false);
        }
    };

    const handleDownloadPDF = async () => {
        const element = document.getElementById("printable-booking-invoice");
        if (!element) return;
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        const filename = `Booking_Invoice_${invoice?.invoiceId || invoiceId}_${timestamp}.pdf`;

        try {
            setDownloadingPdf(true);
            toastify("Generating PDF...", "info");

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: "#ffffff"
            });

            const imgData = canvas.toDataURL("image/jpeg", 1.0);
            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4"
            });

            const imgWidth = 210;
            const pageHeight = 297;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let position = 0;

            pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight, "", "FAST");

            let heightLeft = imgHeight - pageHeight;
            while (heightLeft >= 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight, "", "FAST");
                heightLeft -= pageHeight;
            }

            pdf.save(filename);
            toastify("PDF downloaded successfully!", "success");
        } catch (error) {
            toastify("Failed to generate PDF. Please try again.", "error");
        } finally {
            setDownloadingPdf(false);
        }
    };

    const items = invoice ? [
        {
            description: `Accommodation - ${invoice.unitDetails?.name || "Unit"}`,
            note: `(${invoice.calculatedData?.nights || 0} nights)`,
            unitPrice: invoice.basePrice || 0,
            quantity: invoice.calculatedData?.nights || 0
        },
        ...((invoice.additionalServices || []).map((service) => ({
            description: service.serviceName,
            note: "Additional Service",
            unitPrice: service.price,
            quantity: service.quantity || 1
        })))
    ] : [];

    const subtotal = invoice
        ? (invoice.calculatedData?.subtotal || 0) + (invoice.calculatedData?.additionalServicesTotal || 0)
        : 0;

    const invoiceActionOptions = [
        { label: "Download Invoice", value: "download", icon: "ti ti-download" },
        ...(shouldShowRecordPayment()
            ? [{ label: "Record Payment", value: "record-payment", icon: "ti ti-cash" }]
            : [])
    ];

    const invoiceActionItemTemplate = (option) => (
        <div className="d-flex align-items-center">
            <i className={option.icon} style={{ marginRight: "10px" }}></i>
            <span>{option.label}</span>
        </div>
    );

    const handleInvoiceActionChange = (e) => {
        const action = e.value;
        setSelectedAction(action);

        switch (action) {
            case "download":
                handleDownloadPDF();
                break;
            case "record-payment":
                setShowRecordPaymentDialog(true);
                break;
            default:
                break;
        }

        setTimeout(() => setSelectedAction(null), 500);
    };

    const dialogFooter = (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
                <Dropdown
                    value={selectedAction}
                    options={invoiceActionOptions}
                    onChange={handleInvoiceActionChange}
                    placeholder="Invoice Actions"
                    itemTemplate={invoiceActionItemTemplate}
                    valueTemplate={(option, props) => (option ? invoiceActionItemTemplate(option) : <span>{props.placeholder}</span>)}
                    className="w-100"
                    style={{ minWidth: "180px" }}
                    disabled={!invoice || downloadingPdf}
                />
                {downloadingPdf && <i className="pi pi-spin pi-spinner"></i>}
            </div>

            <div className="d-flex gap-2 flex-wrap">
                <Button label="Close" icon="ti ti-x" className="p-button-text" onClick={onHide} />
                {shouldShowPayNow() && (
                    <Button
                        label="Pay Now"
                        icon="ti ti-credit-card"
                        className="p-button-success"
                        onClick={() => setShowPaymentModal(true)}
                    />
                )}
                {shouldShowCheckIn() && (
                    <Button
                        label={checkingIn ? "Checking In..." : "Check In"}
                        icon="ti ti-login"
                        className="p-button-info"
                        onClick={handleCheckIn}
                        disabled={checkingIn}
                    />
                )}
            </div>
        </div>
    );

    return (
        <>
            <Dialog
                header="Booking Invoice"
                visible={visible}
                style={{ width: "95vw", maxWidth: "880px" }}
                onHide={onHide}
                footer={dialogFooter}
                maximizable
            >
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center py-5">
                        <div className="spinner-border text-primary me-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="text-muted">Loading invoice details...</span>
                    </div>
                ) : !invoice ? (
                    <div className="text-center py-5">
                        <div className="text-danger mb-3" style={{ fontSize: "3rem" }}>
                            <i className="ti ti-alert-triangle"></i>
                        </div>
                        <h4 className="text-dark mb-2">Invoice Not Found</h4>
                        <p className="text-muted">Unable to load invoice details.</p>
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <div className={styles.invoiceContainer} style={{ minHeight: "auto" }}>
                            <div id="printable-booking-invoice" className={styles.invoiceContent}>
                                {/* Status Banner */}
                                <div
                                    className={styles.statusBanner}
                                    data-status={(invoice.paymentStatus || invoice.status || "").toLowerCase()}
                                >
                                    <span>{invoice.paymentStatus || invoice.status}</span>
                                </div>

                                <div className={styles.invoiceHeader}>
                                    <div className={styles.invoiceHeaderContent}>
                                        <div className={styles.headerLeft}>
                                            <h1
                                                className={styles.invoiceTitleInHeader}
                                                style={{
                                                    marginTop: "-20px",
                                                    marginLeft: "60%",
                                                    color: "white",
                                                    fontSize: "28px",
                                                    fontWeight: "600"
                                                }}
                                            >
                                                INVOICE
                                            </h1>
                                            {facilityLogo && (
                                                <div
                                                    className={styles.logoContainer}
                                                    style={{ marginTop: "0", marginLeft: "60%" }}
                                                >
                                                    <img src={facilityLogo} alt="Company Logo" className={styles.companyLogo} />
                                                </div>
                                            )}
                                        </div>
                                        <div className={styles.headerDetails}>
                                            <div className={styles.detailRow}>
                                                <span className={styles.label}>INVOICE NO.</span>
                                                <span className={styles.value}>{invoice.invoiceId}</span>
                                            </div>
                                            <div className={styles.detailRow}>
                                                <span className={styles.label}>ACCOUNT NO.</span>
                                                <span className={styles.value}>{invoice.accountNumber}</span>
                                            </div>
                                            <div className={styles.detailRow}>
                                                <span className={styles.label}>INVOICE DATE</span>
                                                <span className={styles.value}>{formatDate(invoice.issueDate)}</span>
                                            </div>
                                            <div className={styles.detailRow}>
                                                <span className={styles.label}>DUE DATE</span>
                                                <span className={styles.value}>{formatDate(invoice.dueDate)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.invoiceBody}>
                                    <div className={styles.addressSection}>
                                        <div className={styles.addressLayout}>
                                            <div className={styles.fromSection}>
                                                <h2 className={styles.sectionTitle}>FROM</h2>
                                                <div className={styles.addressContent}>
                                                    <div>{companyDetails?.name || "Company Name"}</div>
                                                    <div>{companyDetails?.address || "Company Address"}</div>
                                                    <div>{companyDetails?.country || "Country"}</div>
                                                </div>
                                            </div>
                                            <div className={styles.billToSection}>
                                                <h2 className={styles.sectionTitle}>BILL TO</h2>
                                                <div className={styles.addressContent}>
                                                    <div>{invoice.guestInfo?.name || "N/A"}</div>
                                                    <div>Unit: {invoice.unitDetails?.name || "N/A"}</div>
                                                    {invoice.guestInfo?.phone && <div>{invoice.guestInfo.phone}</div>}
                                                </div>
                                            </div>
                                            <div className={styles.totalSection}>
                                                <h2 className={styles.sectionTitle}>TOTAL DUE</h2>
                                                <div className={`${styles.totalAmount} ${isFinalCredit ? styles.creditAmount : ""}`}>
                                                    {isFinalCredit
                                                        ? formatAmount(-Math.abs(balanceRemaining))
                                                        : formatAmount(balanceRemaining)}
                                                </div>
                                            </div>
                                        </div>

                                        {invoice.reservationDetails?.servedByName && (
                                            <div style={{ marginTop: "0.75rem", fontSize: "0.875rem", color: "#6b7280" }}>
                                                You were served by <strong>{invoice.reservationDetails.servedByName}</strong>
                                            </div>
                                        )}

                                        {invoice.cancellationPolicy && (
                                            <div style={{ marginTop: "1rem" }}>
                                                <Button
                                                    label={`Cancellation Policy: ${invoice.cancellationPolicy.name}`}
                                                    icon="ti ti-file-text"
                                                    link
                                                    className="p-0 text-primary"
                                                    style={{ textDecoration: "underline", fontSize: "0.875rem" }}
                                                    onClick={() => setShowCancellationPolicyDialog(true)}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.itemsGrid}>
                                        <div className={styles.gridHeader}>
                                            <span className={styles.columnHeader}>DESCRIPTION</span>
                                            <span className={`${styles.columnHeader} ${styles.right}`}>UNIT PRICE</span>
                                            <span className={`${styles.columnHeader} ${styles.right}`}>QTY</span>
                                            <span className={`${styles.columnHeader} ${styles.right}`}>AMOUNT</span>
                                        </div>

                                        {items.map((item, index) => (
                                            <div key={index} className={styles.gridRow}>
                                                <span className={styles.value}>
                                                    {item.description}
                                                    {item.note && (
                                                        <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>{item.note}</div>
                                                    )}
                                                </span>
                                                <span className={`${styles.value} ${styles.right}`}>{formatAmount(item.unitPrice)}</span>
                                                <span className={`${styles.value} ${styles.right}`}>{item.quantity}</span>
                                                <span className={`${styles.value} ${styles.right}`}>
                                                    {formatAmount(item.quantity * item.unitPrice)}
                                                </span>
                                            </div>
                                        ))}

                                        <div className={styles.calculations}>
                                            <div className={styles.calculationsGrid}>
                                                <div className={styles.calculationsRow}>
                                                    <span className={styles.calcLabel}>SUBTOTAL</span>
                                                    <span className={styles.calcValue}>{formatAmount(subtotal)}</span>
                                                </div>
                                                {invoice.calculatedData?.discountAmount > 0 && (
                                                    <div className={styles.calculationsRow}>
                                                        <span className={styles.calcLabel}>DISCOUNT</span>
                                                        <span className={`${styles.calcValue} ${styles.creditAmount}`}>
                                                            -{formatAmount(invoice.calculatedData.discountAmount)}
                                                        </span>
                                                    </div>
                                                )}
                                                <div className={styles.calculationsRow}>
                                                    <span className={styles.calcLabel}>TAX</span>
                                                    <span className={styles.calcValue}>
                                                        {formatAmount(invoice.calculatedData?.taxAmount)}
                                                    </span>
                                                </div>
                                                <div className={styles.calculationsRow}>
                                                    <span className={styles.calcLabel}>INVOICE TOTAL</span>
                                                    <span className={styles.calcValue}>{formatAmount(invoice.totalAmount)}</span>
                                                </div>

                                                <div className={styles.divider}></div>

                                                <div className={styles.calculationsRow}>
                                                    <span className={styles.calcLabel}>PAYMENT</span>
                                                    <span className={styles.calcValue}>
                                                        {formatAmount(invoice.calculatedData?.totalPaid)}
                                                    </span>
                                                </div>

                                                <div className={`${styles.calculationsRow} ${styles.balance}`}>
                                                    <span className={styles.calcLabel}>TOTAL DUE</span>
                                                    <span
                                                        className={`${styles.calcValue} ${isFinalCredit ? styles.creditAmount : styles.debitAmount}`}
                                                    >
                                                        {isFinalCredit
                                                            ? formatAmount(-Math.abs(balanceRemaining))
                                                            : formatAmount(balanceRemaining)}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className={styles.totalRow}>
                                                <span className={styles.totalLabel}>TOTAL DUE</span>
                                                <span
                                                    className={`${styles.totalValue} ${isFinalCredit ? styles.creditAmount : styles.debitAmount}`}
                                                >
                                                    {isFinalCredit
                                                        ? "(" + formatAmount(Math.abs(balanceRemaining)) + ")"
                                                        : formatAmount(balanceRemaining)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        className={styles.footer}
                                        style={{
                                            backgroundColor: "#f0f0f0",
                                            padding: "12px 20px",
                                            marginLeft: "-20px",
                                            marginRight: "-20px",
                                            marginBottom: "-20px",
                                            borderBottomLeftRadius: "8px",
                                            borderBottomRightRadius: "8px"
                                        }}
                                    >
                                        {invoice.accountNumber ? (
                                            <p style={{ textAlign: "center", fontSize: "11px", marginBottom: 0 }}>
                                                To complete your payment, use paybill{" "}
                                                <strong>{invoice.paymentDetails?.paybillNumber || "N/A"}</strong> with account number{" "}
                                                <strong>{invoice.accountNumber}</strong>.
                                            </p>
                                        ) : (
                                            <p style={{ textAlign: "center", fontSize: "12px", marginBottom: 0, color: "#6c757d" }}>
                                                Payment Method Not Configured
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>

            {showPaymentModal && invoice && (
                <PaymentModal
                    visible={showPaymentModal}
                    onHide={() => setShowPaymentModal(false)}
                    accountNumber={invoice.accountNumber || invoice.invoiceId}
                    facilityId={facilityId}
                    customerId={null}
                    amount={balanceRemaining}
                    phoneNumber={invoice.guestInfo?.phone || ""}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                />
            )}

            {showRecordPaymentDialog && invoice && (
                <RecordPaymentDialog
                    visible={showRecordPaymentDialog}
                    onHide={() => setShowRecordPaymentDialog(false)}
                    invoice={invoice}
                    onPaymentSuccess={handleRecordPaymentSuccess}
                />
            )}

            {invoice?.cancellationPolicy && (
                <Dialog
                    header={`Cancellation Policy: ${invoice.cancellationPolicy.name}`}
                    visible={showCancellationPolicyDialog}
                    style={{ width: "600px", maxWidth: "95vw" }}
                    onHide={() => setShowCancellationPolicyDialog(false)}
                >
                    {invoice.cancellationPolicy.description && (
                        <p>{invoice.cancellationPolicy.description}</p>
                    )}

                    {invoice.cancellationPolicy.isNonRefundable ? (
                        <div className="alert alert-danger">
                            This booking is non-refundable. No refund is given under any circumstance.
                        </div>
                    ) : (
                        <>
                            <h6>Refund Rules</h6>
                            <ul>
                                {(invoice.cancellationPolicy.refundRules || []).map((rule, index) => (
                                    <li key={index}>
                                        {rule.refundPercentage}% refund if cancelled at least {rule.daysBeforeCheckIn} day(s) before check-in
                                        {rule.description ? ` - ${rule.description}` : ""}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </Dialog>
            )}
        </>
    );
};

export default BookingInvoiceDialog;
